import { AuthStatus } from "@aws-amplify/ui";
import HeaderImage from "../assets/baptize-america-header.svg";
import { USAddress } from "baptize-america-client";
import { FetchUserAttributesOutput } from "aws-amplify/auth";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

const CUSTOM_CHURCH_ID = "custom:churchId";
const ADMIN_CHURCH_ID = "ADMIN";

export const getChurchId = (attributes: FetchUserAttributesOutput): string => {
  if (!attributes) return "";
  return attributes[CUSTOM_CHURCH_ID] ?? "";
};

export const isAdmin = (attributes: FetchUserAttributesOutput) => {
  if (!attributes) return false;
  return attributes[CUSTOM_CHURCH_ID] === ADMIN_CHURCH_ID;
};

export const isAttendee = (attributes: FetchUserAttributesOutput): boolean => {
  if (!attributes) return false;
  return !attributes[CUSTOM_CHURCH_ID];
};

export const isAuthenticated = (authStatus: AuthStatus) => {
  return authStatus && authStatus === "authenticated";
};

export const getRole = (churchId: string | undefined) => {
  if (churchId === undefined || churchId === "") {
    return "Regular";
  }
  if (churchId.startsWith("C-")) {
    return "Church Manager";
  }
  if (churchId === ADMIN_CHURCH_ID) {
    return "Admin";
  }
  return "";
};
/**
 * Checks if user is a church manager for this churchId.
 */
export const isChurchManager = (
  attributes: FetchUserAttributesOutput,
  churchId: string | undefined,
) => {
  if (!attributes || !churchId) return false;
  return attributes[CUSTOM_CHURCH_ID] === churchId;
};

/**
 * Checks if user's account role type is a church manager.
 */
//todo this should just be  if (!userChurchId.startsWith("C-")) return;
export const isManagerRole = (attributes: FetchUserAttributesOutput) => {
  if (!attributes) return false;
  const userChurchId: string = attributes[CUSTOM_CHURCH_ID] ?? "";
  return userChurchId.startsWith("C-") || userChurchId.startsWith("CHURCH");
};

const hostPark = "baptize-america.kev.daniel-eichman.com";
const hostEichman = "baptize-america.beta.daniel-eichman.com";
const hostProd = "app.baptize.org";

export const configMap: { [key: string]: { auth: any; baseUrl: string } } = {
  [hostPark]: {
    auth: {
      Cognito: {
        identityPoolId: "",
        userPoolId: "us-east-1_YL8wiuGKe",
        userPoolClientId: "335einap663gd0cngg9e9sft31",
      },
    },
    baseUrl: `https://api.${hostPark}`,
  },
  [hostEichman]: {
    auth: {
      Cognito: {
        identityPoolId: "",
        userPoolId: "us-east-2_yC2Lo3OVP",
        userPoolClientId: "54ku7784carfmr9ucd95cg54vl",
      },
    },
    baseUrl: `https://api.${hostEichman}`,
  },
  [hostProd]: {
    auth: {
      Cognito: {
        identityPoolId: "",
        userPoolId: "us-west-2_NTj15AiIG",
        userPoolClientId: "44rcb9dtglf0hiafa1b854tl1g",
      },
    },
    baseUrl: `https://api.${hostProd}`,
  },
};

if (window.location.port === "3000") {
  configMap[window.location.hostname] = configMap[hostPark];
}

if (window.location.port === "3001") {
  configMap[window.location.hostname] = configMap[hostEichman];
}

export const getEnvVar = (name: string) => {
  const value = process.env[name];
  if (!value) {
    console.warn(`Missing required environment variable: ${name}`);
  }
  return value;
};
export type SortOrder = "asc" | "desc";

export function handleSort<T>(
  property: keyof T,
  currentOrderBy: keyof T,
  currentOrder: SortOrder,
  setOrder: (order: SortOrder) => void,
  setOrderBy: (orderBy: keyof T) => void,
  setData: (updater: (prevData: T[]) => T[]) => void,
) {
  const isAsc = currentOrderBy === property && currentOrder === "asc";
  const newOrder: SortOrder = isAsc ? "desc" : "asc";

  setOrder(newOrder);
  setOrderBy(property);

  setData((prevData) => {
    if (!prevData) return prevData;

    const direction = newOrder === "asc" ? 1 : -1;

    return [...prevData].sort((a, b) => {
      const aValue = a[property];
      const bValue = b[property];

      if (aValue == null) return 1 * direction;
      if (bValue == null) return -1 * direction;

      const aDate = new Date(aValue as any);
      const bDate = new Date(bValue as any);
      const aIsDate = !isNaN(aDate.getTime());
      const bIsDate = !isNaN(bDate.getTime());

      if (aIsDate && bIsDate) {
        return direction * (aDate.getTime() - bDate.getTime());
      }

      if (!isNaN(Number(aValue)) && !isNaN(Number(bValue))) {
        return direction * Number(aValue) - Number(bValue);
      }

      return direction * aValue.toString().localeCompare(bValue.toString());
    });
  });
}

export const dateFromDateTimeTzStr = (
  date: string,
  time: string,
  tz: string,
): Date | undefined => {
  // format of: dateStr = "2025/05/07";
  // format of: timeStr = "14:30:00";
  // format of: tzStr = "America/New_York";
  dayjs.extend(utc);
  dayjs.extend(timezone);
  if (!time || !date || !tz) return undefined;

  const fullStr = `${date} ${time}`;
  const dayJsDate = dayjs.tz(fullStr, "YYYY/MM/DD HH:mm:ss", tz);
  return dayJsDate.toDate();
};

export const dayJsTimeFromDateTx = (date: Date, tz: string) => {
  // format of: tzStr = "America/New_York";
  dayjs.extend(utc);
  dayjs.extend(timezone);
  if (!date || !tz) return;

  return dayjs.tz(date, tz);
};

export const stringFromDateTz = (date: Date, tz: string) => {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: tz,
  };

  const parts = new Intl.DateTimeFormat("en", options).formatToParts(date);

  const year = parts.find((p) => p.type === "year")?.value;
  const month = parts.find((p) => p.type === "month")?.value;
  const day = parts.find((p) => p.type === "day")?.value;

  return `${year}-${month}-${day}`; // MM/DD/YYYY
};

//This should match Utils.humanReadableTime()
export const humaneReadableTime = (date: Date, timezone: string) => {
  try {
    const options: Intl.DateTimeFormatOptions = {
      weekday: "short",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone: timezone,
      timeZoneName: "short",
      hour12: true,
    };

    const formatter = new Intl.DateTimeFormat("en-US", options);
    const parts = formatter.formatToParts(date);

    // Extract the necessary parts from the formatted date
    let weekday = "";
    let month = "";
    let day = "";
    let year = "";
    let hour = "";
    let minute = "";
    let ampm = "";
    let timeZoneName = "";

    for (const part of parts) {
      switch (part.type) {
        case "weekday":
          weekday = part.value;
          break;
        case "month":
          month = part.value;
          break;
        case "day":
          day = part.value;
          break;
        case "year":
          year = part.value;
          break;
        case "hour":
          hour = part.value;
          break;
        case "minute":
          minute = part.value;
          break;
        case "dayPeriod":
          ampm = part.value;
          break;
        case "timeZoneName":
          timeZoneName = part.value;
          break;
      }
    }

    return `${weekday} (${month}/${day}/${year}) @ ${hour}:${minute} ${ampm} ${timeZoneName}`;
  } catch (error) {
    throw new Error("Invalid timezone provided");
  }
};

export const humaneReadableShortTime = (date: Date, timezone: string) => {
  try {
    const options: Intl.DateTimeFormatOptions = {
      weekday: "short",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone: timezone,
      timeZoneName: "short",
      hour12: true,
    };

    const formatter = new Intl.DateTimeFormat("en-US", options);
    const parts = formatter.formatToParts(date);

    // Extract the necessary parts from the formatted date
    let month = "";
    let day = "";
    let hour = "";
    let minute = "";
    let ampm = "";
    let timeZoneName = "";

    for (const part of parts) {
      switch (part.type) {
        case "month":
          month = part.value;
          break;
        case "day":
          day = part.value;
          break;
        case "hour":
          hour = part.value;
          break;
        case "minute":
          minute = part.value;
          break;
        case "dayPeriod":
          ampm = part.value;
          break;
        case "timeZoneName":
          timeZoneName = part.value;
          break;
      }
    }

    return `${month}/${day} ${hour}:${minute}${ampm} ${timeZoneName}`;
  } catch (error) {
    throw new Error("Invalid timezone provided");
  }
};

export const chipTime = (date: Date, timezone: string) => {
  try {
    const options: Intl.DateTimeFormatOptions = {
      weekday: "short",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone: timezone,
      timeZoneName: "short",
      hour12: true,
    };

    const formatter = new Intl.DateTimeFormat("en-US", options);
    const parts = formatter.formatToParts(date);

    // Extract the necessary parts from the formatted date
    let hour = "";
    let minute = "";
    let ampm = "";
    let weekday = "";

    for (const part of parts) {
      switch (part.type) {
        case "weekday":
          weekday = part.value;
          break;
        case "hour":
          hour = part.value;
          break;
        case "minute":
          minute = part.value;
          break;
        case "dayPeriod":
          ampm = part.value;
          break;
      }
    }

    return `${weekday} ${hour}:${minute}${ampm}`;
  } catch (error) {
    throw new Error("Invalid timezone provided");
  }
};

// returns full address joined as single string
export const formatAddress = (address: USAddress, mapMarker = false) => {
  let result = null;
  if (address === undefined || address?.streetAddress1 === undefined) {
    return result;
  }
  const { streetAddress1, streetAddress2, city, state, postalCode } = address;
  const line2 = city + ", " + state + ", " + postalCode;
  if (mapMarker) {
    return streetAddress2
      ? `${streetAddress1}
        ${streetAddress2}
        ${line2}`
      : `${streetAddress1}
        ${line2}`;
  }
  result = streetAddress2
    ? `${streetAddress1}, ${streetAddress2}, ${line2}`
    : `${streetAddress1}, ${line2}`;

  return result;
};

export const isNumber = (value: string) => {
  return /^-?\d+(\.\d+)?$/.test(value);
};

/**
 * Generates "Confirmed Registration" Email template
 * to send when users registers for baptism event.
 * @param param0
 * @returns {String} Email template as HTML
 */
export const getConfirmRegisterEmail = ({
  name,
  churchName,
  churchAddress,
  eventDate,
  churchService,
}: {
  name: string;
  churchName: string;
  churchAddress: string;
  eventDate: string;
  churchService: string;
}) => {
  return `<!DOCTYPE html>
    <html>
    <head>
      <style>
        body {
          margin: 0;
          padding: 0;
          width: 100%;
          height: 100%;
          background-color: #fdf5e3;
        }
        .body-container {
          width: 100%;
          min-height: 100vh;
          background-color: #fdf5e3;
          padding: 2rem;
          box-sizing: border-box;
        }
        .logo-table {
          width: 100%;
          max-width: 200px;
          margin: 0 auto 1rem auto;
        }
        .logo {
          text-align: center;
          padding: 18px;
        }
        .logo img {
          width: 100%;
          height: auto;
        }
        .email-table {
          width: 100%;
          max-width: 400px;
          background-color: #fff;
          font-family: sans-serif;
          border-radius: 1rem;
          box-shadow: rgba(0, 0, 0, 0.05) -4px 9px 50px -6px;
          margin: 0 auto;
        }
        .content-container {
          padding: 2rem;
        }
        .title {
          font-size: 12px;
          font-weight: bold;
          margin-bottom: 10px;
        }
        .content {
          font-size: 12px;
          margin-bottom: 20px;
        }
        .cta {
          font-size: 12px;
          text-align: center;
          margin-top: 40px;
        }
        .footer {
          color: #7a7a86;
          font-size: 10px;
          text-align: center;
          margin-top: 40px;
        }
        a {
          color: #00169E;
          text-decoration: none;
        }
        a:hover {
          text-decoration: underline;
        }
        .qr-code {
          display: block;
          margin: 0 auto;
          margin-top: 2rem;
          margin-bottom: 2rem;
          max-width: 200px;
          box-shadow: rgba(0, 0, 0, 0.1) 0px 0px 5px 0px, rgba(0, 0, 0, 0.1) 0px 0px 1px 0px;
          padding: 24px;
          border-radius: 0.5rem;
          height: auto;
        }
      </style>
    </head>
    <body>
      <div class="body-container">
        <!-- Logo Table -->
        <table class="logo-table" cellpadding="0" cellspacing="0">
          <tr>
            <td class="logo">
              <img src=${HeaderImage} alt="Logo">
            </td>
          </tr>
        </table>
        <!-- Email Table -->
        <table class="email-table" cellpadding="0" cellspacing="0">
          <tr>
            <td class="content-container">
              <p>Hi ${name},</p>
              <p>We are so excited that you've chosen to take this incredible step of faith! Your baptism on Pentecost Sunday is going to be a powerful experience.</p>
              <p><strong>Here's your confirmation:</strong></p>
              <ul>
                <li><strong>Location:</strong> ${churchName}</li>
                <li><strong>Address:</strong> ${churchAddress}</li>
                <li><strong>Date:</strong> ${eventDate}</li>
                <li><strong>Service Time:</strong> ${churchService}</li>
              </ul>
              <p><strong>What to expect on Baptism Day:</strong></p>
              <ul>
                <li><strong>Arrive early:</strong> Plan to arrive at least 15 before the service begins.</li>
                <li><strong>What to bring:</strong> A towel, a change of clothes, and a joyful heart!</li>
                <li><strong>Check-In:</strong> When you arrive, please scan your QR code for fast and easy check-in.</li>
              </ul>
              <p>Congrats on making a decision, alongside of 1000s of others to get baptized this Pentecost Sunday!</p>
              <p>Thanks!<br>Baptize America Team</p>
              <p class="cta"><a href="https://www.instagram.com/baptizeamerica/"
                rel="noopener noreferrer"><strong>Instagram</strong></a> | <a href="https://www.baptize.org"
                rel="noopener noreferrer"><strong>Website</strong></a></p>
            </td>
          </tr>
        </table>
      </div>
    </body>
    </html>`;
};

export function isoTimestamp() {
  // eg: 2025-05-12T05:50
  return new Date().toISOString().slice(0, 16);
}

export const convertToCSV = <T extends Record<string, any>>(data: T[]) => {
  const escapeCSVValue = (value: string) => {
    if (value.includes(",") || value.includes('"')) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  };

  const headers = Object.keys(data[0]).map(escapeCSVValue).join(",");
  const rows = data.map((row) =>
    Object.values(row).map(escapeCSVValue).join(","),
  );
  return [headers, ...rows].join("\n");
};

export const downloadCSV = <T extends Record<string, any>>(
  data: T[],
  fileName: string = "registrations",
) => {
  const csvContent = convertToCSV(data ?? []);
  const blob = new Blob([csvContent], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${fileName}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(function () {
    window.URL.revokeObjectURL(url);
  }, 3000);
};

export const containsUpperCase = (text: string) => {
  return /[A-Z]/.test(text);
};
